import { gql, useQuery } from "@apollo/client";
import { faChevronDown, faChevronUp, faPlay, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useApplicationContext } from "components/Application";
import { CenterSpinner } from "components/CenterSpinner";
import { Footer } from "components/Footer";
import { ItemCartRow } from "components/ItemCartRow";
import { ItemHeading } from "components/ItemHeading";
import { capitalize } from "lodash";
import React, { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import "./Items.css";
import "twin.macro";

const articleItemQuery = gql`
  query($itemId: ID!) {
    articleItem(id: $itemId) {
      id
      code
      nameEn
      nameIt
      shortDescriptionEn
      shortDescriptionIt
      longDescriptionEn
      longDescriptionIt
      garmentCareEn
      garmentCareIt
      modelInfoEn
      modelInfoIt
      cover
      colorGuide
      mainVideo
      available
      brand {
        id
        nameEn
        nameIt
      }
      season {
        id
        code
        nameEn
        nameIt
        kindEn
        kindIt
        isEnabled
        isStock
        position
      }
      line {
        id
        code
        color
        coverHorizontal
        coverVertical
        nameEn
        nameIt
        descriptionEn
        descriptionIt
      }
      sizeGroup {
        id
        code
        sizes {
          id
          code
          value
        }
      }
      fitting {
        id
        code
        value
      }
      internalComposition {
        id
        code
        nameEn
        nameIt
      }
      externalComposition {
        id
        code
        nameEn
        nameIt
        descriptionEn
        descriptionIt
      }
      color {
        id
        code
        nameEn
        nameIt
        patch
      }
      kind {
        id
        code
        nameEn
        nameIt
      }
      images {
        id
        image
        position
      }
      stocks {
        quantity
        size {
          id
        }
      }
      variants {
        id
        code
        available
        color {
          id
          code
          nameEn
          nameIt
          patch
        }
        images {
          id
          image
          position
        }
        stocks {
          quantity
          size {
            id
          }
        }
      }
      automaticSuggestedItemsForFabric {
        id
        code
        nameEn
        nameIt
        cover
        brand {
          id
        }
        season {
          id
        }
        referenceItem {
          id
        }
      }
      automaticSuggestedItemsForModel {
        id
        code
        nameEn
        nameIt
        cover
        brand {
          id
        }
        season {
          id
        }
        referenceItem {
          id
        }
      }
      suggestedItems {
        id
        code
        nameEn
        nameIt
        cover
        brand {
          id
        }
        season {
          id
        }
        referenceItem {
          id
        }
      }
    }
  }
`;

export const Show: React.FC<{}> = () => {
  const {
    siteEnvironment: { appEndpointStore },
    session,
  } = useApplicationContext();
  const { currentUser } = session;
  const { priceList } = currentUser;
  const { prices } = priceList;

  const { itemId } = useParams();

  const [activeImage, setActiveImage] = useState<string | false>(false);
  const [activeVideo, setVideo] = useState<string | false>(false);
  const [activeLightBox, toggleLightBox] = useState<string | false>(false);
  const [infoCollapsed, setInfoCollapsed] = useState<boolean>(true);

  const variables = { itemId };
  const { loading, data } = useQuery(articleItemQuery, { variables, fetchPolicy: "cache-and-network" });

  const { i18n } = useTranslation();
  const languageSuffix = capitalize(i18n.language);

  if (loading) return <CenterSpinner size="large" />;

  const { articleItem: item } = data;
  const {
    brand,
    season,
    line,
    suggestedItems,
    automaticSuggestedItemsForFabric,
    automaticSuggestedItemsForModel,
    sizeGroup: { sizes },
  } = item;

  let formattedWholesale: string | false = false;
  let formattedWholesalePromo: string | false = false;
  let formattedRetail: string | false = false;
  prices.forEach((price: any) => {
    if (price.itemId === item.id) {
      formattedWholesale = price.formattedWholesale;
      formattedWholesalePromo = price.formattedWholesalePromo;
      formattedRetail = price.formattedRetail;
    }
  });

  let orderable = false;
  if (formattedWholesale) {
    orderable = true;
  }

  let variants: any[] = [];
  variants.push(item);
  item.variants.forEach((variant: any) => variants.push(variant));

  let images: string[] = [];
  variants.forEach((variant: any) => variant.images.forEach((image: any) => images.push(image.image)));
  if (item.colorGuide) images.push(item.colorGuide);

  if (images.length === 0) images.push(item.cover);

  const downArrowPresent = images.length > 4;

  const video = item.mainVideo;

  const fabric_suggested =
    automaticSuggestedItemsForFabric.lenght > 0 ? suggestedItems : automaticSuggestedItemsForFabric;
  const model_suggested = automaticSuggestedItemsForModel.lenght > 0 ? suggestedItems : automaticSuggestedItemsForModel;

  return (
    <Fragment>
      {activeLightBox && (
        <div
          tw="fixed w-screen overflow-scroll h-screen z-10 -mt-16 pt-20 pb-40 bg-white"
          onClick={() => {
            toggleLightBox(false);
          }}
        >
          <div tw="cursor-pointer fixed text-2xl -mt-16 z-20" style={{ right: "1rem" }}>
            <FontAwesomeIcon tw="text-gray-900" icon={faTimes} />
          </div>
          {activeVideo && (
            <video
              loop
              playsInline
              autoPlay
              tw="w-full h-full lg:object-scale-down lg:object-center object-contain object-center"
            >
              <source src={activeLightBox} type="video/mp4" />
            </video>
          )}
          {!activeVideo && (
            <img src={activeLightBox} alt={item[`name${languageSuffix}`]} tw="m-0 m-auto lg:h-auto h-full z-20" />
          )}
        </div>
      )}
      <div tw="flex space-x-2">
        <div tw="flex flex-col space-x-2 lg:w-full">
          <ItemHeading brand={brand} season={season} line={line} item={item} />

          <div tw="flex px-4 my-6 space-x-4">
            <div className="itemMediaBox" tw="flex lg:w-full w-2/5 space-x-5">
              <div tw="flex relative flex-col lg:w-1/12 w-1/6 space-y-5 overflow-y-auto">
                {video && (
                  <div
                    key="video"
                    tw="relative cursor-pointer"
                    onClick={() => {
                      setVideo(video);
                      setActiveImage(false);
                    }}
                  >
                    <div tw="absolute text-2xl" style={{ marginTop: "15%", marginLeft: "30%", zIndex: 1 }}>
                      <FontAwesomeIcon tw="text-gray-100" icon={faPlay} />
                    </div>
                    <video src={`${appEndpointStore}${video}`}></video>
                  </div>
                )}
                {images.map((image: any, index: number) => (
                  <div
                    key={index}
                    tw="cursor-pointer"
                    onClick={() => {
                      setActiveImage(image);
                      setVideo(false);
                    }}
                  >
                    <img src={`${appEndpointStore}${image}`} alt={item[`name${languageSuffix}`]} tw="w-full" />
                  </div>
                ))}
                {downArrowPresent && (
                  <div tw="sticky w-full text-center bg-white text-gray-900" style={{ bottom: "-2px" }}>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </div>
                )}
              </div>
              {!activeVideo && (
                <div tw="flex lg:w-11/12 w-5/6 ">
                  <img
                    src={`${appEndpointStore}${activeImage ? activeImage : images[0]}`}
                    alt={item[`name${languageSuffix}`]}
                    tw="w-auto lg:object-scale-down lg:object-left object-contain object-left-top"
                    style={{ cursor: "zoom-in" }}
                    onClick={() => {
                      toggleLightBox(`${appEndpointStore}${activeImage ? activeImage : images[0]}`);
                    }}
                  />
                </div>
              )}
              {activeVideo && (
                <div tw="flex w-11/12 relative">
                  <video
                    onClick={() => {
                      toggleLightBox(`${appEndpointStore}${activeVideo}`);
                    }}
                    style={{ cursor: "zoom-in" }}
                    loop
                    playsInline
                    autoPlay
                    tw="w-11/12 lg:object-scale-down lg:object-left object-contain object-left-top"
                  >
                    <source src={`${appEndpointStore}${activeImage ? activeImage : video}`} type="video/mp4" />
                  </video>
                </div>
              )}
            </div>

            <div tw="flex lg:w-full w-3/5 justify-end">
              <div tw="flex flex-col space-y-4 lg:w-full">

                <div tw="flex flex-col space-y-4 pr-8">
                  <h1 tw="text-gray-900 text-4xl leading-none">{item[`name${languageSuffix}`]}</h1>
                  <h2 tw="text-gray-900 text-2xl leading-none">
                    {item[`shortDescription${languageSuffix}`]}
                  </h2>
                  <h3 tw="text-gray-900 text-xl">
                    {item[`code`].replace('D-','').replace('U-','').split(" ", 1)}
                  </h3>
                </div>

                <div tw="flex flex-col pr-8">
                  <p style={{ width: "fit-content" }} tw="text-white bg-gray-900 px-2 mb-0">{i18n.language === "it" ? "DESCRIZIONE" : "DESCRIPTION:"}</p>
                  <p tw="text-gray-900 mb-0">{item[`longDescription${languageSuffix}`]}</p>
                </div>

                { line && (
                  <div tw="flex flex-col pr-8">
                    <p style={{ width: "fit-content" }} tw="text-white bg-gray-900 px-2 text-sm mb-0">{line[`name${languageSuffix}`]}</p>
                    <p tw="text-gray-900 text-sm mb-0">{line[`description${languageSuffix}`]}</p>
                  </div>
                )}

                <div tw="flex flex-col pr-8">
                  <div tw="flex w-full justify-end pt-2">
                    <div tw="flex w-full">
                      <div tw="flex flex-col w-full">
                        <div style={{ width: "fit-content" }} tw="text-white bg-gray-900 px-2 cursor-pointer" onClick={() => setInfoCollapsed(!infoCollapsed)}>
                          <span tw="pr-4">{i18n.language === "it" ? "DETTAGLI" : "DETAILS"}</span>
                          {infoCollapsed && <FontAwesomeIcon icon={faChevronDown} />}
                          {!infoCollapsed && <FontAwesomeIcon icon={faChevronUp} />}
                        </div>
                        {!infoCollapsed && (
                          <Fragment>
                            {item.internalComposition && (
                              <div tw="mt-4">
                                <strong>
                                  {i18n.language === "it" ? "COMPOSIZIONE INTERNA" : "INTERNAL COMPOSITION"}
                                </strong>
                                <br />
                                {capitalize(item.internalComposition[`name${languageSuffix}`])}
                              </div>
                            )}
                            {item.externalComposition[`description${languageSuffix}`] && (
                              <div tw="mt-4">
                                <strong>
                                  {i18n.language === "it" ? "TESSUTO ESTERNO" : "EXTERNAL COMPOSITION"}
                                </strong>
                                <br />
                                {capitalize(item.externalComposition[`description${languageSuffix}`])}
                              </div>
                            )}
                            {item.externalComposition && (
                              <div tw="mt-4">
                                <strong>
                                  {i18n.language === "it" ? "COMPOSIZIONE ESTERNA" : "EXTERNAL COMPOSITION"}
                                </strong>
                                <br />
                                {item.externalComposition[`name${languageSuffix}`]}
                              </div>
                            )}
                            {item.garmentCareEn && (
                              <div tw="mt-4">
                                <strong>{i18n.language === "it" ? "CURA DEL CAPO" : "GARMENT CARE"}</strong>
                                <br />
                                {capitalize(item[`garmentCare${languageSuffix}`])}
                              </div>
                            )}
                            {item.fitting && (
                              <div tw="mt-4">
                                <strong>{i18n.language === "it" ? "VESTIBILITÀ" : "FITTING"}</strong>
                                <br />
                                {capitalize(item.fitting.code)}
                              </div>
                            )}
                            {item.modelInfoEn && (
                              <div tw="mt-4">
                                <strong>{i18n.language === "it" ? "MISURE MODELLO" : "MODEL INFO"}</strong>
                                <br />
                                {item[`modelInfo${languageSuffix}`]}
                              </div>
                            )}
                          </Fragment>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div tw="flex flex-col pr-8">
                  <div>
                    <p tw="text-gray-900 text-xl mb-0">{formattedWholesale}</p>
                    {formattedWholesalePromo && (
                      <p tw="text-gray-900 text-xl mb-0">
                        {formattedWholesalePromo}{" "}
                        <span tw="text-sm">
                          {i18n.language === "it"
                            ? `acquista 12 pezzi tra tutti i modelli realizzati in tessuto ${item.externalComposition.code} e questo sarà il prezzo wholesale con MarkUp 3.0. Scopri gli altri modelli che fanno parte della promo tra i suggeriti per tessuto che trovi sotto.`
                            : `buy 12 pieces among all the models made in ${item.externalComposition.code} fabric and this will be the wholesale price with MarkUp 3.0. Discover the other styles that are part of the promo through the fabric suggestions you find below.`}
                        </span>
                      </p>
                    )}
                    {formattedRetail && (
                      <p tw="text-gray-900 ">
                        {formattedRetail} {i18n.language === "it" ? "prezzo al pubblico" : "retail price"}
                      </p>
                    )}
                  </div>

                  <div tw="lg:block hidden">
                    <table style={{ borderCollapse: "initial", borderSpacing: "5px" }}>
                      <tbody>
                        <tr tw="text-center">
                          <th style={{ width: "150px" }}></th>
                          <th style={{ width: "34px", height: "34px" }}></th>
                          {sizes.map((size: any) => (
                            <th key={size.id} style={{ width: "34px", height: "34px" }}>
                              {size.code}
                            </th>
                          ))}
                          <th></th>
                          <th></th>
                        </tr>
                      </tbody>
                    </table>
                    {variants.map((variant: any) => (
                      <div
                        onClick={() => {
                          setVideo(false);
                        }}
                      >
                        <ItemCartRow
                          key={variant.id}
                          item={item}
                          variant={variant}
                          sizes={sizes}
                          stock={season.isStock}
                          setActiveImage={setActiveImage}
                          orderable={orderable}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div tw="lg:hidden inline-block w-auto px-8">
            <table style={{ borderCollapse: "initial", borderSpacing: "5px" }}>
              <tbody>
                <tr tw="text-center">
                  <th style={{ width: "150px" }}></th>
                  <th style={{ width: "34px", height: "34px" }}></th>
                  {sizes.map((size: any) => (
                    <th key={size.id} style={{ width: "34px", height: "34px" }}>
                      {size.code}
                    </th>
                  ))}
                  <th></th>
                  <th></th>
                </tr>
              </tbody>
            </table>

            {variants.map((variant: any) => (
              <div
                onClick={() => {
                  setVideo(false);
                }}
              >
                <ItemCartRow
                  key={variant.id}
                  item={item}
                  variant={variant}
                  sizes={sizes}
                  stock={season.isStock}
                  setActiveImage={setActiveImage}
                  orderable={orderable}
                />
              </div>
            ))}
          </div>

          <div tw="flex px-4 my-6 mt-24 w-full">
            <div tw="flex w-full space-x-5">
              {fabric_suggested.length > 0 && (
                <div tw="flex flex-col w-full">
                  <div tw="w-full text-center">
                    {i18n.language === "it" ? "I NOSTRI SUGGERIMENTI PER TESSUTO" : "OUR FABRIC SUGGESTIONS"}
                  </div>
                  <div tw="grid lg:grid-cols-8 grid-cols-5 lg:gap-4 gap-2 lg:mr-4 mr-8 mt-8 lg:mt-4">
                    {fabric_suggested.map((suggestedItem: any) => (
                      <Link
                        key={suggestedItem.id}
                        to={`/b/${suggestedItem.brand.id}/s/${suggestedItem.season.id}/i/${
                          suggestedItem.referenceItem?.id ?? suggestedItem.id
                        }`}
                        tw="block relative w-full mb-4 p-4 text-center cursor-pointer hover:(shadow-lg text-gray-900)"
                        onClick={() => {
                          setActiveImage(suggestedItem.cover);
                          setVideo(false);
                        }}
                      >
                        <img
                          src={`${appEndpointStore}${suggestedItem.cover}`}
                          alt={suggestedItem[`name${languageSuffix}`]}
                          tw="w-full"
                        />
                        <p>{suggestedItem[`name${languageSuffix}`]}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {model_suggested.length > 0 && (
            <div tw="flex px-4 my-6 w-full">
              <div tw="flex w-full space-x-5">
                <div tw="flex flex-col w-full">
                  <div tw="w-full text-center">
                    {i18n.language === "it" ? "I NOSTRI SUGGERIMENTI PER MODELLO" : "OUR MODEL SUGGESTIONS"}
                  </div>
                  <div tw="grid lg:grid-cols-8 grid-cols-5 lg:gap-4 gap-2 lg:mr-4 mr-8 mt-8 lg:mt-4">
                    {model_suggested.map((suggestedItem: any) => (
                      <Link
                        key={suggestedItem.id}
                        to={`/b/${suggestedItem.brand.id}/s/${suggestedItem.season.id}/i/${
                          suggestedItem.referenceItem?.id ?? suggestedItem.id
                        }`}
                        tw="block relative w-full mb-4 p-4 text-center cursor-pointer hover:(shadow-lg text-gray-900)"
                        onClick={() => {
                          setActiveImage(suggestedItem.cover);
                          setVideo(false);
                        }}
                      >
                        <img
                          src={`${appEndpointStore}${suggestedItem.cover}`}
                          alt={suggestedItem[`name${languageSuffix}`]}
                          tw="w-full"
                        />
                        <p>{suggestedItem[`name${languageSuffix}`]}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <Footer />
        </div>
      </div>
    </Fragment>
  );
};
